/** Настройки наборов: запись, объявления и закрытие. */

import type { ReactNode } from 'react';

import { Checkbox, Field, Select, TextInput } from '../../components/ui.tsx';
import { settingsTabPath } from '../../lib/settings-tabs.ts';
import { useLinkHandler } from '../../lib/router.ts';
import { Section, type SettingsTabProps } from './shared.tsx';

/** Выключатель с подписью — их на этой вкладке несколько подряд. */
function Toggle({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
  hint?: string;
}): ReactNode {
  return (
    <label className="flex items-start gap-3">
      <Checkbox checked={checked} onChange={onChange} />
      <span className="text-sm">
        {label}
        {hint !== undefined && (
          <span className="block text-xs" style={{ color: 'var(--text-muted)' }}>
            {hint}
          </span>
        )}
      </span>
    </label>
  );
}

export function EventsTab({ draft, update }: SettingsTabProps): ReactNode {
  const onLinkClick = useLinkHandler();
  const reminderEnabled = draft.notifications['event.reminder'].enabled;

  return (
    <>
      <Section title="Запись в набор">
        <Field
          label="Куда попадает плюс"
          hint="Если основной состав уже полон, человек всё равно попадёт в замену"
        >
          <Select
            value={draft.eventJoinTarget}
            onChange={(event) => {
              const value = event.target.value;
              if (value === 'plus' || value === 'reserve') {
                update('eventJoinTarget', value);
              }
            }}
          >
            <option value="reserve">В замену — состав собирает ростер-менеджер</option>
            <option value="plus">Сразу в основной состав, пока есть места</option>
          </Select>
        </Field>

        <Field label="Кого зовёт анонс набора" hint="Упоминание уходит один раз, при создании набора">
          <Select
            value={draft.eventAnnounceMention}
            onChange={(event) => {
              const value = event.target.value;
              if (value === 'everyone' || value === 'here' || value === 'none') {
                update('eventAnnounceMention', value);
              }
            }}
          >
            <option value="everyone">@everyone — всех</option>
            <option value="here">@here — только тех, кто в сети</option>
            <option value="none">Без упоминания</option>
          </Select>
        </Field>
      </Section>

      <Section
        title="Объявления об уходе"
        description="Отдельное сообщение в канале плюсов, когда человек убирает плюс."
      >
        <Toggle
          checked={draft.eventAnnounceLeftMain}
          onChange={(checked) => {
            update('eventAnnounceLeftMain', checked);
          }}
          label="Объявлять уход из основного состава"
          hint="Место освободилось — это стоит заметить сразу"
        />

        <Toggle
          checked={draft.eventAnnounceLeftReserve}
          onChange={(checked) => {
            update('eventAnnounceLeftReserve', checked);
          }}
          label="Объявлять уход из замены"
          hint="Обычно не нужно: в замене место ничего не стоит, а канал зарастает сообщениями"
        />
      </Section>

      <Section title="Ход набора">
        <Toggle
          checked={draft.autoCloseOnStart}
          onChange={(checked) => {
            update('autoCloseOnStart', checked);
          }}
          label="Закрывать набор автоматически в момент начала мероприятия"
        />

        <Toggle
          checked={draft.eventReopenOnTimeChange}
          onChange={(checked) => {
            update('eventReopenOnTimeChange', checked);
          }}
          label="Открывать закрытый набор заново, если время перенесли на будущее"
          hint="Иначе перенесённое мероприятие останется без возможности записаться"
        />

        {/* Выключателя напоминания здесь нет: он живёт рядом с текстом, на
            вкладке личных сообщений. Два места для одного и того же
            расходятся — забудешь про второе, и выключенное продолжит
            уходить. */}
        <Field
          label="За сколько минут напоминать"
          hint={
            reminderEnabled
              ? 'Само напоминание включено на вкладке «Личные сообщения»'
              : 'Напоминание выключено — включается на вкладке «Личные сообщения»'
          }
        >
          <TextInput
            type="number"
            min={1}
            max={1440}
            value={draft.reminderMinutesBefore}
            disabled={!reminderEnabled}
            onChange={(event) => {
              update('reminderMinutesBefore', Number.parseInt(event.target.value, 10) || 1);
            }}
          />
        </Field>

        {!reminderEnabled && (
          <a
            href={settingsTabPath('dm')}
            onClick={onLinkClick}
            className="text-sm underline"
            style={{ color: 'var(--accent)' }}
          >
            Перейти к личным сообщениям
          </a>
        )}
      </Section>

      <Section title="Сбор в голосовом">
        <Toggle
          checked={draft.voiceCheckEnabled}
          onChange={(checked) => {
            update('voiceCheckEnabled', checked);
          }}
          label="Проверять перед началом, кто из основы в голосовом"
          hint="Отсутствующих бот зовёт упоминанием, сидящих в других каналах переносит к остальным"
        />

        <Field label="За сколько минут проверять сбор">
          <TextInput
            type="number"
            min={1}
            max={120}
            value={draft.voiceCheckMinutesBefore}
            disabled={!draft.voiceCheckEnabled}
            onChange={(event) => {
              update('voiceCheckMinutesBefore', Number.parseInt(event.target.value, 10) || 1);
            }}
          />
        </Field>
      </Section>
    </>
  );
}
